import type {
  ReactiveController,
  ReactiveControllerHost,
  ReactiveElement,
} from 'lit';

/**
 * The SlotHelper ReactiveController.
 *
 * This controller provides utilities for testing and managing slots in Web Components.
 * It allows you to:
 * - Test if a named slot is present/populated
 * - Test if the default slot is present/populated
 * - Get a list of all available slots
 *
 * @example
 * // Initialize the SlotHelper in your Web Component
 * class MyElement extends LitElement {
 *   slots = new SlotHelper(this);
 *
 *   // Use the SlotHelper methods in your component's logic
 *   render() {
 *     return html`
 *       ${this.slots.isNotEmpty('example-slot') ?
 *          html`<div class="example-slot--wrapper">
 *            <slot name="example-slot"></slot>
 *          </div>` :
 *        nothing }
 *      `
 *   }
 * }
 *
 * Methods:
 * - isNotEmpty(slotName?: string): boolean
 *   Tests if a slot is present and populated.
 *   If no slotName is provided, it tests the default slot.
 *
 * - getSlotNames(): string[]
 *   Returns an array of all available slot names.
 *
 * Properties:
 * - slotNames: string[]
 *   An array containing the names of all available slots.
 *
 * - hasDefaultSlotContent: boolean
 *   Indicates whether the host element has default slot content.
 *
 * - hasNamedSlotContent: boolean
 *   Indicates whether the host element has named slot content.
 *
 * Usage examples:
 *
 * 1. Test if a specific named slot exists:
 *    if (this.slots.isNotEmpty('header')) {
 *      console.log('Header slot exists and is populated');
 *    }
 *
 * 2. Test if the default slot exists:
 *    if (this.slots.isNotEmpty()) {
 *      console.log('Default slot exists and is populated');
 *    }
 *
 * 3. Get all available slot names:
 *    const availableSlots = this.slots.getSlotNames();
 *    console.log('Available slots:', availableSlots);
 *
 *
 * Note: The SlotHelper automatically updates when slot content changes,
 * ensuring that your component always has the latest slot information.
 *
 * ⠀⠀⠀⠀⠀⠀⣠⣞⠙⡶⢄⣠⡞⠋⠳⡶⢤⡀⠀⣠⡴⠦⣄⣀⡴⣶⠤⡄⠀⠀
 * ⠀⠀⠀⠀⠀⣰⡏⠘⣆⡗⣸⡟⠀⠀⠀⣿⢮⣧⣰⠋⠀⠀⠸⣝⣶⢡⠔⠻⡆⠀
 * ⠀⠀⠀⠀⠀⣿⠀⠀⠛⢳⠋⡇⠀⠀⠀⢩⠻⠋⡏⠀⠀⠀⠀⡿⡿⠃⠀⠀⢹⡀
 * ⠀⠀⠀⠀⠀⡟⠀⠀⠀⢸⠀⣷⠀⠀⠀⠸⡀⠈⣧⠀⠀⠀⠀⣇⢻⠀⠀⠀⠘⡇
 * ⠀⢀⣠⡴⢶⠟⠶⢦⣄⠀⢇⢸⡄⠀⠀⠀⢡⡀⢻⡀⠀⠀⠀⠸⣿⠀⠀⠀⠀⣿
 * ⢠⣿⠝⠉⠁⠀⠺⣧⠹⢧⣬⡄⣇⠀⠀⠀⠀⢧⠘⣇⠀⠀⠀⠀⠹⡄⠀⠀⠀⣿
 * ⡞⣁⣤⠀⠰⢟⡰⠂⠀⢸⠻⢦⡏⠀⠀⠀⢀⠸⡤⢿⡄⠀⠀⠀⠀⠹⣆⠀⠀⣿
 * ⢷⠁⢹⠉⠉⠉⠀⢀⠤⢃⡠⠊⠀⠀⠀⢠⠈⡗⠃⢸⠃⠀⠀⠀⠀⠀⠸⡄⢀⡟
 * ⠘⣧⣇⣀⣀⠤⠐⠁⠀⠀⠀⠀⠀⠀⠑⠺⢿⠁⠀⠎⠀⠀⠀⠀⠀⠀⠀⠁⣸⠃
 * ⠀⠘⣧⡀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⢰⡏⠀
 * ⠀⠀⠈⢿⣦⡀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⣰⠟⠀⠀
 * ⠀⠀⠀⠀⠈⠻⣷⣤⡀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⠀⢀⣠⡼⠋⠀⠀⠀
 * ⠀⠀⠀⠀⠀⠀⠀⠉⠙⠻⠶⢶⣤⣤⣤⣤⣤⣤⣤⣤⣴⠶⠞⠋⠁⠀⠀⠀⠀⠀
 *
 */
export class SlotHelper implements ReactiveController {
  host: ReactiveControllerHost & Element;
  hostEl: ReactiveElement;
  slotNames: string[] = [];
  hasDefaultSlotContent: boolean;
  hasNamedSlotContent: boolean;

  constructor(host: ReactiveControllerHost & Element) {
    (this.host = host).addController(this);
    this.hostEl = this.host as unknown as ReactiveElement;
    this.handleSlotChange = this.handleSlotChange.bind(this);
  }

  /**
   * Checks if the host element has content in its default slot.
   * @private
   * @returns {boolean} True if the default slot contains content, false otherwise.
   */
  private hasDefaultSlot() {
    for (const node of this.host.childNodes) {
      if (node.nodeType === Node.TEXT_NODE && node.textContent!.trim() !== '') {
        return (this.hasDefaultSlotContent = true);
      }
      if (
        node.nodeType === Node.ELEMENT_NODE &&
        !(node as Element).hasAttribute('slot')
      ) {
        return (this.hasDefaultSlotContent = true);
      }
    }
    return (this.hasDefaultSlotContent = false);
  }

  /**
   * Checks if the host element has a named slot.
   * @private
   * @param {string} name - The name of the slot to check for.
   * @returns {boolean} True if the named slot is found, false otherwise.
   */
  private hasNamedSlot(name: string) {
    return (this.hasNamedSlotContent =
      this.host.querySelector(`:scope > [slot="${name}"]`) !== null);
  }

  /**
   * Tests for the presence of a slot.
   * @param {string} [slotName] - The name of the slot to test. If undefined, tests for the default slot.
   * @returns {boolean} True if the slot exists and is populated, false otherwise.
   */
  isNotEmpty(slotName?: string | undefined) {
    return slotName === undefined
      ? this.hasDefaultSlot()
      : this.hasNamedSlot(slotName);
  }

  /**
   * Lifecycle method called when the host element is connected to the DOM.
   * Sets up the slotchange event listener and initializes the slot names.
   */
  hostConnected() {
    this.host.shadowRoot!.addEventListener('slotchange', this.handleSlotChange);
    this.slotNames = this.getSlotNames();
  }

  /**
   * Lifecycle method called when the host element is disconnected from the DOM.
   * Removes the slotchange event listener.
   */
  hostDisconnected() {
    this.host.shadowRoot!.removeEventListener(
      'slotchange',
      this.handleSlotChange
    );
  }

  /**
   * Handles the slotchange event.
   * @param {Event} event - The slotchange event object.
   */
  handleSlotChange(event: Event) {
    const slot = event.target as HTMLSlotElement;

    // Update slotNames in case slot content changes.
    this.slotNames = this.getSlotNames();

    if (
      (this.hasDefaultSlotContent && !slot.name) ||
      (slot.name && this.slotNames.includes(slot.name))
    ) {
      this.host.requestUpdate();
    }
  }

  /**
   * Returns an array of slot names present in the host element,
   * including "default slot" if a default slot is present.
   * @returns {string[]} An array of slot names.
   */
  getSlotNames() {
    const slots: string[] = [];
    const namedSlotElements = this.host.querySelectorAll('[slot]');
    this.hasNamedSlotContent = namedSlotElements.length > 0;

    namedSlotElements.forEach((slot: Element) => {
      const name = slot.getAttribute('slot');
      if (name && !slots.includes(name)) {
        slots.push(name);
      }
    });

    // Check for default slot
    if (this.hasDefaultSlot() && !slots.includes('default slot')) {
      slots.unshift('default slot');
    }

    return slots;
  }
}
